import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// 社員複数選択モーダル。
// tricaGo（Kujyu）の社員一覧 API をサーバーサイド分頁・サーバーサイド検索で参照しながら
// チェックボックスで複数選択する。名簿は約29万件あるため全件取得はせず、
// 翻頁・検索のたびに 1 ページ分だけ取得する（検索入力はデバウンス）。
// 選択結果は社員番号+氏名の配列として親へ返す。
import { useEffect, useRef, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import Button from './Button';
import Modal from './Modal';
import Pagination from './Pagination';
import { Spinner } from './Spinner';
// これを超える選択は susugi 側で社員番号ごとの個別リクエストになるため注意を促す
const SELECTION_WARNING_THRESHOLD = 30;
// 検索入力のデバウンス（ms）
const SEARCH_DEBOUNCE_MS = 350;
// 退職済み社員かどうかを判定する（retireDate が過去日なら退職済みとみなす）
function isRetired(raw) {
    const retireDate = raw?.retireDate;
    if (typeof retireDate === 'string' && retireDate.trim() !== '') {
        const parsed = new Date(retireDate);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.getTime() <= Date.now();
        }
    }
    const status = typeof raw?.status === 'string' ? raw.status : '';
    return /retire|退職/i.test(status);
}
function toDirectoryEmployee(raw) {
    const employeeNumber = typeof raw?.employeeNumber === 'string' ? raw.employeeNumber.trim() : '';
    if (!employeeNumber)
        return null;
    const name = [raw?.lastName, raw?.firstName].filter(Boolean).join(' ').trim();
    const nameKana = [raw?.lastNameKana, raw?.firstNameKana].filter(Boolean).join(' ').trim();
    const companyName = typeof raw?.companyName === 'string' ? raw.companyName : '';
    return {
        employeeNumber,
        name: name || employeeNumber,
        nameKana,
        // 上游はデータ欠損を文字列「不明」で返すことがあるため空扱いにする
        companyName: companyName === '不明' ? '' : companyName,
        departmentName: typeof raw?.departmentName === 'string' ? raw.departmentName : '',
        position: typeof raw?.position === 'string' ? raw.position : '',
        hireDate: typeof raw?.hireDate === 'string' ? raw.hireDate.split('-').join('/') : '',
        retired: isRetired(raw),
    };
}
// 社員番号→氏名のモジュールレベルキャッシュ（編集モーダルの氏名解決で共有）
const employeeNameCache = new Map();
// 保存済みの社員番号から氏名を解決する。見つからない番号は番号そのものを氏名として返す。
export async function resolveEmployeesByNumbers(employeeNumbers) {
    return Promise.all(employeeNumbers.map(async (employeeNumber) => {
        const cached = employeeNameCache.get(employeeNumber);
        if (cached) {
            return { employeeNumber, name: cached };
        }
        try {
            const response = await apiClient.kujyu.getEmployeeByCode(employeeNumber);
            const raw = response.success ? response.data : null;
            const name = [raw?.lastName, raw?.firstName].filter(Boolean).join(' ').trim();
            if (name) {
                employeeNameCache.set(employeeNumber, name);
                return { employeeNumber, name };
            }
        }
        catch (error) {
            console.warn(`社員番号 ${employeeNumber} の氏名解決に失敗しました:`, error);
        }
        return { employeeNumber, name: employeeNumber };
    }));
}
const EmployeeSelectorModal = ({ isOpen, onClose, selectedEmployees, onConfirm, zIndexClassName = 'z-[60]' }) => {
    const [rows, setRows] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // 検索ボックスの生値と、デバウンス後に実際へ問い合わせるキーワード
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    // モーダル内の下書き選択（確定ボタンを押すまで親へは反映しない）
    const [draft, setDraft] = useState(new Map());
    // 古いレスポンスが新しい表示を上書きしないようにリクエストへ通し番号を振る
    const requestSeq = useRef(0);
    const fetchPage = async () => {
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const response = searchQuery
                ? await apiClient.kujyu.searchEmployees(searchQuery, page, pageSize)
                : await apiClient.kujyu.listEmployees(page, pageSize, 'employeeNumber', 'ASC');
            if (seq !== requestSeq.current)
                return;
            if (!response.success || !response.data) {
                throw new Error(response.error || '社員一覧の取得に失敗しました');
            }
            // mizu は {success, data: {content, totalElements, totalPages}} を返し、apiClient がそのまま透過する
            const body = response.data;
            if (!Array.isArray(body?.content)) {
                throw new Error('社員一覧のレスポンス形式が不正です');
            }
            const employees = body.content
                .map(toDirectoryEmployee)
                .filter(Boolean);
            employees.forEach((e) => employeeNameCache.set(e.employeeNumber, e.name));
            setRows(employees);
            setTotalElements(Number(body.totalElements) || employees.length);
            setTotalPages(Math.max(1, Number(body.totalPages) || 1));
        }
        catch (e) {
            if (seq !== requestSeq.current)
                return;
            setError(e.message || '社員一覧の取得に失敗しました');
        }
        finally {
            if (seq === requestSeq.current) {
                setLoading(false);
            }
        }
    };
    // 検索入力をデバウンスして問い合わせキーワードへ反映する
    useEffect(() => {
        if (!isOpen)
            return;
        const timer = setTimeout(() => {
            setSearchQuery(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);
    // ページ・件数・検索キーワードが変わるたびにサーバーへ問い合わせる
    useEffect(() => {
        if (!isOpen)
            return;
        fetchPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, page, pageSize, searchQuery]);
    // 開くたびに親の選択値から下書きを復元し、フィルタを初期化する
    useEffect(() => {
        if (!isOpen)
            return;
        setDraft(new Map(selectedEmployees.map((e) => [e.employeeNumber, e])));
        setSearchInput('');
        setSearchQuery('');
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    const selectableRows = rows.filter((e) => !e.retired);
    const isAllPageSelected = selectableRows.length > 0 && selectableRows.every((e) => draft.has(e.employeeNumber));
    const toggleEmployee = (employee) => {
        setDraft((prev) => {
            const next = new Map(prev);
            if (next.has(employee.employeeNumber)) {
                next.delete(employee.employeeNumber);
            }
            else {
                next.set(employee.employeeNumber, {
                    employeeNumber: employee.employeeNumber,
                    name: employee.name,
                });
            }
            return next;
        });
    };
    const togglePageAll = () => {
        setDraft((prev) => {
            const next = new Map(prev);
            if (isAllPageSelected) {
                selectableRows.forEach((e) => next.delete(e.employeeNumber));
            }
            else {
                selectableRows.forEach((e) => next.set(e.employeeNumber, { employeeNumber: e.employeeNumber, name: e.name }));
            }
            return next;
        });
    };
    const removeSelected = (employeeNumber) => {
        setDraft((prev) => {
            const next = new Map(prev);
            next.delete(employeeNumber);
            return next;
        });
    };
    const draftList = Array.from(draft.values());
    return (_jsx(Modal, { isOpen: isOpen, onClose: onClose, size: "xl", zIndexClassName: zIndexClassName, title: "\uD83D\uDC65 \u5BFE\u8C61\u793E\u54E1\u3092\u9078\u629E", footer: _jsxs("div", { className: "w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3", children: [_jsx("div", { className: "text-xs text-gray-500", children: draftList.length > SELECTION_WARNING_THRESHOLD ? (_jsx("span", { className: "text-amber-700", children: "\u26A0\uFE0F \u9078\u629E\u4EBA\u6570\u304C\u591A\u3044\u3068\u53D6\u5F97\u306B\u6642\u9593\u304C\u304B\u304B\u308A\u307E\u3059\u3002\u5168\u54E1\u304C\u5BFE\u8C61\u306A\u3089\u672A\u9078\u629E\uFF08\u5168\u793E\u54E1\uFF09\u306E\u65B9\u304C\u9AD8\u901F\u3067\u3059\u3002" })) : (_jsx("span", { children: "\u672A\u9078\u629E\u306E\u307E\u307E\u78BA\u5B9A\u3059\u308B\u3068\u5168\u793E\u54E1\u304C\u5BFE\u8C61\u306B\u306A\u308A\u307E\u3059\u3002" })) }), _jsxs("div", { className: "flex items-center gap-2 justify-end", children: [_jsx(Button, { variant: "secondary", onClick: onClose, children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), _jsxs(Button, { variant: "primary", onClick: () => {
                                onConfirm(draftList);
                                onClose();
                            }, children: ["\u78BA\u5B9A", draftList.length > 0 ? `（${draftList.length}名）` : '（全社員）'] })] })] }), children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex flex-col sm:flex-row gap-2", children: [_jsx("input", { type: "text", value: searchInput, onChange: (e) => setSearchInput(e.target.value), placeholder: "\u6C0F\u540D\u30FB\u30AB\u30CA\u30FB\u793E\u54E1\u756A\u53F7\u3067\u691C\u7D22", className: "flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" }), _jsx(Button, { variant: "secondary", onClick: () => setDraft(new Map()), disabled: draftList.length === 0, className: "whitespace-nowrap", children: "\u9078\u629E\u3092\u30AF\u30EA\u30A2" })] }), draftList.length > 0 && (_jsxs("div", { className: "border border-indigo-200 bg-indigo-50 rounded-md p-3", children: [_jsxs("div", { className: "text-xs font-medium text-indigo-900 mb-2", children: ["\u9078\u629E\u4E2D\uFF08", draftList.length, "\u540D\uFF09"] }), _jsx("div", { className: "flex flex-wrap gap-1.5 max-h-24 overflow-y-auto", children: draftList.map((e) => (_jsxs("span", { className: "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800", children: [e.name, _jsx("button", { type: "button", onClick: () => removeSelected(e.employeeNumber), "aria-label": `${e.name}の選択を解除`, className: "ml-1 text-indigo-600 hover:text-indigo-900 focus:outline-none", children: "\u00D7" })] }, e.employeeNumber))) })] })), loading && (_jsxs("div", { className: "flex items-center justify-center py-12 text-sm text-gray-600", children: [_jsx(Spinner, { size: "sm", className: "mr-3" }), "\u793E\u54E1\u4E00\u89A7\u3092\u53D6\u5F97\u4E2D..."] })), !loading && error && (_jsx("div", { className: "py-12 text-center", children: _jsxs("div", { className: "text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-4 inline-block", children: [_jsx("div", { className: "font-medium mb-1", children: "\u793E\u54E1\u4E00\u89A7\u306E\u53D6\u5F97\u306B\u5931\u6557\u3057\u307E\u3057\u305F" }), _jsx("div", { className: "text-xs text-red-600 mb-3", children: error }), _jsx("button", { type: "button", onClick: fetchPage, className: "text-sm text-red-600 hover:text-red-800 underline", children: "\u518D\u8A66\u884C" })] }) })), !loading && !error && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "border border-gray-200 rounded-md overflow-hidden", children: [_jsxs("div", { className: "flex items-center justify-between bg-gray-50 border-b border-gray-200 px-3 py-2", children: [_jsxs("label", { className: "flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer", children: [_jsx("input", { type: "checkbox", checked: isAllPageSelected, onChange: togglePageAll, disabled: selectableRows.length === 0, className: "w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" }), "\u3053\u306E\u30DA\u30FC\u30B8\u3092\u5168\u9078\u629E"] }), _jsxs("span", { className: "text-xs text-gray-500", children: [searchQuery ? '該当' : '全', " ", totalElements.toLocaleString(), "\u540D"] })] }), rows.length === 0 ? (_jsx("div", { className: "py-10 text-center text-sm text-gray-500", children: "\u6761\u4EF6\u306B\u4E00\u81F4\u3059\u308B\u793E\u54E1\u304C\u3044\u307E\u305B\u3093" })) : (_jsx("ul", { className: "divide-y divide-gray-100 max-h-[40vh] overflow-y-auto", children: rows.map((e) => {
                                        const checked = draft.has(e.employeeNumber);
                                        return (_jsx("li", { children: _jsxs("label", { className: `flex items-center gap-3 px-3 py-2 ${e.retired
                                                    ? 'cursor-not-allowed opacity-60'
                                                    : `cursor-pointer ${checked ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}`, children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => toggleEmployee(e), disabled: e.retired, className: "w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" }), _jsx("span", { className: "w-24 flex-shrink-0 text-xs font-mono text-gray-500", children: e.employeeNumber }), _jsxs("span", { className: "flex-1 min-w-0", children: [_jsx("span", { className: "block text-sm text-gray-900 truncate", children: e.name }), e.nameKana && (_jsx("span", { className: "block text-xs text-gray-400 truncate", children: e.nameKana }))] }), _jsxs("span", { className: "hidden sm:block w-48 flex-shrink-0 min-w-0 text-right", children: [e.companyName && (_jsx("span", { className: "block text-xs text-gray-700 truncate", children: e.companyName })), _jsx("span", { className: "block text-xs text-gray-400 truncate", children: [e.departmentName, e.position].filter(Boolean).join(' / ') })] }), _jsx("span", { className: "hidden md:block w-24 flex-shrink-0 text-xs text-gray-500 text-right", children: e.hireDate && (_jsxs(_Fragment, { children: [_jsx("span", { className: "block text-gray-400", children: "\u5165\u793E" }), e.hireDate] })) }), _jsx("span", { className: "w-12 flex-shrink-0 text-right", children: _jsx("span", { className: `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${e.retired
                                                                ? 'bg-gray-200 text-gray-600'
                                                                : 'bg-green-100 text-green-700'}`, children: e.retired ? '退職' : '在職' }) })] }) }, e.employeeNumber));
                                    }) }))] }), _jsx(Pagination, { currentPage: page, totalPages: totalPages, totalItems: totalElements, pageSize: pageSize, onPageChange: setPage, pageSizeOptions: [20, 50, 100], onPageSizeChange: (size) => {
                                setPageSize(size);
                                setPage(1);
                            } })] }))] }) }));
};
// フォーム側の「対象社員」表示。選択人数 + 先頭数名のチップ + 選択ボタンをまとめて描画する。
// colorScheme は埋め込み先の配色に合わせる（例: ワークフロー作成の社内データ取得区块は green）。
const SUMMARY_SCHEME_CLASSES = {
    indigo: {
        button: 'text-indigo-700 border-indigo-300 hover:bg-indigo-50 focus:ring-indigo-500',
        chip: 'bg-indigo-100 text-indigo-800',
    },
    green: {
        button: 'text-green-700 border-green-300 hover:bg-green-50 focus:ring-green-500',
        chip: 'bg-green-100 text-green-700',
    },
};
export const EmployeeSelectionSummary = ({ employees, onOpen, maxChips = 3, colorScheme = 'indigo' }) => {
    const visible = employees.slice(0, maxChips);
    const restCount = employees.length - visible.length;
    const scheme = SUMMARY_SCHEME_CLASSES[colorScheme];
    return (_jsxs("div", { children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("button", { type: "button", onClick: onOpen, className: `px-2.5 py-1.5 text-xs font-medium bg-white border rounded-md whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-1 ${scheme.button}`, children: ["\uD83D\uDC65 ", employees.length > 0 ? '選択を変更' : '社員を選択'] }), _jsx("span", { className: "text-sm text-gray-700", children: employees.length > 0
                            ? `${employees.length}名の社員を選択済み`
                            : '全社員が対象（未指定）' })] }), employees.length > 0 && (_jsxs("div", { className: "flex flex-wrap items-center gap-1.5 mt-2", children: [visible.map((e) => (_jsx("span", { className: `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${scheme.chip}`, children: e.name }, e.employeeNumber))), restCount > 0 && _jsxs("span", { className: "text-xs text-gray-500", children: ["\u4ED6", restCount, "\u540D"] })] }))] }));
};
export default EmployeeSelectorModal;
